/**
 * 漫画编辑器画布区：唯一纵向滚动口 + 话次块内吸顶标题。
 * 规范：{@link ScrollRegion} 承担滚动；吸顶条实心底；
 * 画布叠层须 {@link ThemeLayerCrossfade} `fadeOnly`（祖先勿 transform，否则 sticky 失效）。
 * 叠层数值见 {@link COMIC_CANVAS_LAYER}，勿在子组件散落 z-index。
 */

/**
 * 画布叠层规范（单话次块 `isolate` 内相对层级；话次块之间用 section `zIndex` 递增推高）。
 * 页内媒体叠层见 {@link ComicPagePreviewCard}，须 ≤ PAGE_MEDIA_MAX。
 */
export const COMIC_CANVAS_LAYER = {
  /** 话次块在滚动轨道中的叠序起点（低于 Modal/Toast） */
  EPISODE_SECTION_BASE: 10,
  /** 单话次块内：分页轨道 */
  EPISODE_CONTENT: 0,
  /** 单话次块内：吸顶标题（须高于本话分页与页内媒体） */
  EPISODE_STICKY_HEADER: 5,
  /** 页内媒体叠层上限 */
  PAGE_MEDIA_MAX: 4,
} as const

const EPISODE_SECTION_Z_MAX_OFFSET = 89

/** 话次块在滚动轨道中的叠序（后话推高前话吸顶条） */
export function comicEpisodeCanvasSectionZIndex(episodeIndex: number): number {
  return (
    COMIC_CANVAS_LAYER.EPISODE_SECTION_BASE +
    Math.min(Math.max(0, episodeIndex), EPISODE_SECTION_Z_MAX_OFFSET)
  )
}

/** 画布滚动骨架外层（配合 {@link ScrollRegion}） */
export function comicCanvasScrollShellClass(): string {
  return [
    'comic-editor-canvas-scroll',
    'min-h-0 min-w-0 flex-1',
    'pb-3 sm:pb-4 lg:pb-8',
  ].join(' ')
}

/** 画布分页等内容区水平内边距（吸顶标题不含，须全宽） */
export function comicCanvasContentInsetClass(): string {
  return 'px-4 sm:px-6 lg:px-8'
}

/** 滚动轨道：话次块纵向栈；顶内边距在轨道侧，吸顶标题可贴齐滚动口顶缘 */
export const comicCanvasScrollTrackClass =
  'flex flex-col gap-10 pt-3 pb-6 sm:pt-4 lg:pt-8'

/** 单话次块：建立独立叠层上下文，叠序由 {@link comicEpisodeCanvasSectionZIndex} 控制 */
export function comicEpisodeCanvasSectionClass(): string {
  return 'relative isolate min-w-0'
}

/**
 * 话次标题吸顶：随块滚入视口顶缘后固定，下一话标题推走上一话。
 * 使用静态 `z-[5]`（对应 {@link COMIC_CANVAS_LAYER.EPISODE_STICKY_HEADER}，Tailwind 无法扫描动态模板）。
 */
export function comicEpisodeCanvasStickyHeaderClass(angular: boolean): string {
  return [
    'sticky top-0 z-[5]',
    'mb-4 flex w-full flex-nowrap items-center justify-between gap-3 border-b border-[var(--shell-nav-border)] bg-[var(--shell-content-bg)] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-8',
    angular ? 'border-b-2 shadow-[0_2px_0_0_var(--line)]' : 'shadow-[0_1px_0_0_var(--line)]',
  ].join(' ')
}

/** 话次分页轨道：须低于同块吸顶标题（`z-0` 对应 {@link COMIC_CANVAS_LAYER.EPISODE_CONTENT}） */
export function comicEpisodeCanvasContentTrackClass(): string {
  return ['relative z-0 flex flex-col gap-5', comicCanvasContentInsetClass()].join(' ')
}
